import { useLayoutEffect, useRef, useState } from "react";
import { throttle } from "lodash";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

const TEE_TIME_SLOT_WRAP_MAX = 480;

interface IUseDivisionBreakpointsOptions {
  useViewportBreakpoints?: boolean;
}

/**
 * Tracks teesheet division layout breakpoints.
 * Uses viewport width in single-division view and division column width in side-by-side view.
 */
export function useDivisionBreakpoints(
  navigationKey?: string,
  options?: IUseDivisionBreakpointsOptions,
) {
  const useViewportBreakpoints = options?.useViewportBreakpoints === true;
  const divisionRef = useRef<HTMLDivElement>(null);
  const [divisionWidth, setDivisionWidth] = useState<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number | null>(() =>
    typeof window !== "undefined" ? window.innerWidth : null,
  );

  const layoutWidth = useViewportBreakpoints ? viewportWidth : divisionWidth;
  const mobileSizedDivision = layoutWidth == null || layoutWidth <= MOBILE_WIDTH;
  const tabletSizedDivision =
    layoutWidth != null && layoutWidth > MOBILE_WIDTH && layoutWidth <= TABLET_WIDTH;
  const slotWrapSizedDivision = layoutWidth != null && layoutWidth <= TEE_TIME_SLOT_WRAP_MAX;
  const tabletBookingParticipantLayout = tabletSizedDivision || slotWrapSizedDivision;
  const compactTabletSizedDivision =
    tabletSizedDivision && layoutWidth != null && layoutWidth < 925;
  const showTabletBookingNoteIcon =
    (tabletSizedDivision && layoutWidth != null && layoutWidth >= 950) || slotWrapSizedDivision;

  useLayoutEffect(() => {
    if (!useViewportBreakpoints) {
      return;
    }

    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth);

    return () => {
      window.removeEventListener("resize", updateViewportWidth);
    };
  }, [useViewportBreakpoints, navigationKey]);

  useLayoutEffect(() => {
    if (!divisionRef.current) {
      return;
    }

    const update = throttle((entries: ResizeObserverEntry[]) => {
      const width = entries[0].contentRect.width;
      window.requestAnimationFrame(() => {
        setDivisionWidth(width);
      });
    }, 50);

    const resizeObserver = new ResizeObserver(update);

    resizeObserver.observe(divisionRef.current);
    return () => {
      resizeObserver.disconnect();
      update.cancel();
    };
  }, [navigationKey]);

  return {
    divisionRef,
    divisionWidth,
    useViewportBreakpoints,
    mobileSizedDivision,
    tabletSizedDivision,
    compactTabletSizedDivision,
    showTabletBookingNoteIcon,
    slotWrapSizedDivision,
    tabletBookingParticipantLayout,
  };
}
